class BusinessPurchaseTerms extends HTMLElement {
  connectedCallback() {
    this.purpose = this.querySelector('[data-purchase-purpose]');
    this.accepted = this.querySelector('[data-business-accepted]');
    this.panel = this.querySelector('[data-business-acceptance]');
    this.error = this.querySelector('[data-business-error]');
    this.changeHandler = () => this.updatePurpose();
    this.submitHandler = (event) => this.onSubmit(event);
    this.purpose.addEventListener('change', this.changeHandler);
    document.addEventListener('submit', this.submitHandler, true);
    this.updatePurpose();
  }

  disconnectedCallback() {
    this.purpose?.removeEventListener('change', this.changeHandler);
    document.removeEventListener('submit', this.submitHandler, true);
  }

  updatePurpose() {
    const business = this.purpose.value === 'Business';
    this.panel.hidden = !business;
    this.accepted.disabled = !business;
    this.accepted.required = business;
    this.accepted.checked = false;
    this.error.hidden = true;
  }

  acknowledgement() {
    if (!['Business', 'Personal'].includes(this.purpose.value)) return null;
    if (this.purpose.value === 'Business' && !this.accepted.checked) return null;
    const business = this.purpose.value === 'Business';
    return {
      'Purchase purpose': this.purpose.value,
      'Business terms version': business ? this.dataset.version : '',
      'Business terms accepted at UTC': business ? new Date().toISOString() : '',
      'Business terms URL': business ? `${window.location.origin}/pages/business-terms` : '',
    };
  }

  async onSubmit(event) {
    if (event.target.id !== 'cart' || event.submitter?.name !== 'checkout') return;
    if (this.allowNativeSubmit) {
      this.allowNativeSubmit = false;
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    if (this.saving) return;
    const attributes = this.acknowledgement();
    if (!attributes) {
      const control = this.purpose.value === 'Business' ? this.accepted : this.purpose;
      control.reportValidity();
      control.focus();
      return;
    }
    this.saving = true;
    const button = event.submitter;
    const purposeDisabled = this.purpose.disabled;
    const acceptanceDisabled = this.accepted.disabled;
    this.purpose.disabled = true;
    this.accepted.disabled = true;
    button.disabled = true;
    this.error.hidden = true;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(this.dataset.updateUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes }),
        signal: controller.signal,
      });
      if (!response.ok) throw new Error('Cart update rejected');
      const cart = await response.json();
      const matches = Object.entries(attributes).every(([key, value]) =>
        value === '' ? !cart.attributes?.[key] : cart.attributes?.[key] === value
      );
      if (!matches) throw new Error('Cart acknowledgement was not saved');
      if (!Number.isInteger(cart.item_count) || cart.item_count <= 0) throw new Error('Cart is empty');
      if (!this.isConnected || !event.target.isConnected) throw new Error('Cart was replaced');
      this.purpose.disabled = purposeDisabled;
      this.accepted.disabled = acceptanceDisabled;
      button.disabled = false;
      // Preserve pending quantities, notes and native validation with the checkout submitter.
      this.allowNativeSubmit = true;
      event.target.requestSubmit(button);
    } catch {
      this.error.textContent = window.businessTermsMessages?.saveError || 'We could not save your purchase details. Please try again before checkout.';
      this.error.hidden = false;
      this.error.focus();
    } finally {
      clearTimeout(timer);
      this.allowNativeSubmit = false;
      this.purpose.disabled = purposeDisabled;
      this.accepted.disabled = acceptanceDisabled;
      this.saving = false;
      button.disabled = false;
    }
  }
}

if (!customElements.get('business-purchase-terms')) {
  customElements.define('business-purchase-terms', BusinessPurchaseTerms);
}
